/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useMemo } from 'react';
import {
	DROPDOWN_ITEM,
	BUTTON,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	CUSTOM_ITEM_DROPDOWN_ACTION,
	CUSTOM_ITEM_BUTTON,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { MEATBALL_DROPDOWN } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-id.tsx';
import {
	PROJECT,
	ROADMAP,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-type.tsx';
import { LEVEL_THREE } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type {
	UseAnalyticsL3,
	ViewParams,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-jpd-nav-views/src/controllers/analytics/types.tsx';
import type { NavigationCollection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-jpd-nav-views/src/common/types.tsx';
import type { ItemId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-jpd-nav-views/src/common/constants/analytics/item-id.tsx';

export const useRoadmapsAnalyticsL3: UseAnalyticsL3<NavigationCollection> = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const commonAttributes = useMemo(
		() => ({
			level: LEVEL_THREE,
		}),
		[],
	);

	// actions inside meatball menu on projects
	const sendMeatBallDropDownOnProjectsMenuItemAnalyticsEvent = useCallback(
		({ itemId, container }: { itemId: ItemId; container: NavigationCollection }) => {
			const { collectionUUID } = container;

			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: DROPDOWN_ITEM,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM_DROPDOWN_ACTION,
				itemId,
				parentItemId: collectionUUID,
				parentItemType: PROJECT,
			};
			fireUIAnalytics(event, 'roadmapsNavigationMenuItem', attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	// meatball menu on views
	const sendMeatBallDropDownOnViewsAnalyticsEvent = useCallback(
		({ container, viewParams }: { container: NavigationCollection; viewParams: ViewParams }) => {
			const { collectionUUID } = container;

			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: BUTTON,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM_BUTTON,
				itemId: MEATBALL_DROPDOWN,
				collectionUUID,
				parentItemId: viewParams.viewId,
				parentItemType: viewParams.viewKind,
			};
			fireUIAnalytics(event, 'roadmapsNavigationMenuItem', attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	// actions inside create view menu on projects
	const sendCreateViewDropDownOnProjectMenuItemAnalyticsEvent = useCallback(
		({
			itemId,
			container,
			viewParams,
		}: {
			itemId: ItemId;
			container: NavigationCollection;
			viewParams: ViewParams;
		}) => {
			const { collectionUUID } = container;

			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: DROPDOWN_ITEM,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM_DROPDOWN_ACTION,
				itemId,
				itemType: viewParams.viewKind,
				parentItemId: collectionUUID,
				parentItemType: ROADMAP,
				collectionUUID,
			};
			fireUIAnalytics(event, 'roadmapsNavigationMenuItem', attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	return {
		sendMeatBallDropDownOnProjectsMenuItemAnalyticsEvent,
		sendMeatBallDropDownOnViewsAnalyticsEvent,
		sendCreateViewDropDownOnProjectMenuItemAnalyticsEvent,
	};
};
